/**
 * screens/SettlementAlerts — Finance
 * ──────────────────────────────────────────────────────────────────────────
 * Custodians leaving with money in hand.
 *
 * One card per OPEN ASSIGNMENT, not per person: someone holding three
 * assignments is three things to settle, each with its own balance and its own
 * consequence. Collapsing them into one row would hide two of them.
 *
 * Gated on the offboarding feed — without it this console cannot know who is
 * leaving, and an empty list would be a false all-clear.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Card,
  EmptyState,
  Loading,
  PageContainer,
  PageHeader,
  StatusPill,
} from '../../components';
import { neutral, status as statusTones } from '../../theme/colors';
import { mono, numeric, sans, size as fontSize, leading } from '../../theme/typography';
import { useAsyncData } from '../../lib/useAsyncData';
import { date } from '../../lib/dates';
import { format } from '../../lib/money';
import { separationState } from '../../lib/status';
import { fetchSettlements } from '../../services/settlement/settlement.service';
import type { Separation } from '../../schemas/settlement';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { SETTLE_COPY, SETTLE_FLOW, SETTLE_STEPS } from './emptyState';

export function SettlementAlertsScreen() {
  const { dataVersion, openModal, setView } = useShell();
  const { gates } = useConfig();

  const load = useCallback(() => fetchSettlements(), []);
  const { data, loading, gate: gateFrom412 } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load settlement alerts',
  });

  // The gate rides in the body with a 200 — see the note in Reconciliation.
  const gate =
    data?.gates.offboard_feed === false || (!data && gates.offboard_feed === false)
      ? 'offboard_feed'
      : gateFrom412;

  if (loading && !data) {
    return (
      <PageContainer width="settlement">
        <Loading />
      </PageContainer>
    );
  }

  const rows = data?.rows ?? [];

  return (
    <PageContainer width="settlement" testID="screen-settle">
      <PageHeader
        title="Settlement alerts"
        subtitle="Custodians leaving with money in hand."
      />

      {gate || rows.length === 0 ? (
        <EmptyState
          eyebrow={SETTLE_COPY.eyebrow}
          title={gate ? 'The offboarding feed is not connected' : SETTLE_COPY.title}
          blurb={SETTLE_COPY.blurb}
          nodes={SETTLE_FLOW}
          flowNote={SETTLE_COPY.flowNote}
          steps={SETTLE_STEPS}
          gate={gate ?? undefined}
          gateNote={gate ? SETTLE_COPY.gateNote : undefined}
          onResolveGate={gate ? () => setView('settings') : undefined}
          footNote={SETTLE_COPY.footNote}
        />
      ) : (
        <View style={styles.list}>
          {rows.map((row) => (
            <SettlementRow
              key={row.id}
              separation={row}
              onSettle={() => openModal({ kind: 'settle', separationId: row.id })}
            />
          ))}
        </View>
      )}
    </PageContainer>
  );
}

/* ── One alert ───────────────────────────────────────────────────────────── */

function SettlementRow({
  separation,
  onSettle,
}: {
  separation: Separation;
  onSettle: () => void;
}) {
  const state = separationState(separation.state);
  const open = separation.state === 'open';

  return (
    <Card padded>
      <View style={styles.row}>
        <View style={styles.text}>
          <View style={styles.titleRow}>
            <Text numberOfLines={1} style={styles.holder}>
              {separation.holder}
            </Text>
            <StatusPill label={state.label} tone={state.tone} capitalize={false} />
          </View>
          <Text style={styles.detail}>
            Last day {date(separation.last_day)} · holding{' '}
            <Text style={styles.amount}>
              {format(separation.open_balance_minor, separation.cur)}
            </Text>{' '}
            on an open assignment
          </Text>
        </View>

        {open ? (
          <Button label="Settle now" size="lg" variant="primary" onPress={onSettle} />
        ) : (
          <Text style={styles.settled}>Settled</Text>
        )}
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  // 10px between alert cards, from the design source.
  list: { gap: 10 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  text: { flex: 1, minWidth: 220, gap: 3 },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 9, flexWrap: 'wrap' },
  holder: { ...sans('600'), fontSize: fontSize.sm, color: neutral.ink },
  detail: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
  },
  // Inline mono for the amount inside a sentence — still LTR, still tabular.
  amount: { ...mono('400'), ...numeric, color: neutral.grey600 },
  settled: { ...sans('400'), fontSize: fontSize.label, color: statusTones.positive.text },
});

export default SettlementAlertsScreen;
